import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getUploadUrl, uploadDatasetFile } from '../services/api/datasetService';

/**
 * ImportDatasetDialog - Dialog for importing a JSON data file into a dataset
 *
 * Props:
 * - open: Boolean, controls dialog visibility
 * - dataset: Dataset object to import into
 * - onClose: Function called when dialog closes
 * - onImported: Function called when import successfully initiated
 */
export default function ImportDatasetDialog({ open, dataset, onClose, onImported }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState(null);

  function handleClose() {
    if (!uploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadComplete(false);
      setError(null);
      onClose();
    }
  }

  function handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Only JSON files are supported');
      return;
    }

    setSelectedFile(file);
    setError(null);
  }

  async function handleUpload() {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      // Step 1: Get presigned URL
      const { upload_url, import_id } = await getUploadUrl(dataset.dataset_id, selectedFile.name);

      // Step 2: Upload file to S3
      await uploadDatasetFile(upload_url, selectedFile, (progress) => {
        setUploadProgress(progress);
      });

      setUploadComplete(true);
      setUploadProgress(100);

      // Notify parent
      setTimeout(() => {
        onImported({ importId: import_id, dataset });
        handleClose();
      }, 1000);

    } catch (err) {
      console.error('Failed to upload dataset file:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Import Data into {dataset?.dataset_name}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {uploadComplete && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            File uploaded successfully! Processing will begin shortly.
          </Alert>
        )}

        {!uploadComplete && (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select a JSON file containing person records to import.
            </Typography>

            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                bgcolor: selectedFile ? 'action.hover' : 'background.paper',
                '&:hover': !uploading && {
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => !uploading && document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={uploading}
              />

              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />

              {selectedFile ? (
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Click to select a JSON file
                </Typography>
              )}
            </Box>

            {uploading && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </>
        )}

        {!uploadComplete && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="textSecondary">
              Expected format:
            </Typography>
            <List dense>
              <ListItem disablePadding>
                <ListItemText
                  primary="Array of person objects"
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemText
                  primary="Each person must have: person_id, first_name, last_name"
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemText
                  primary="Optional: dataset (will be overridden), image (URL to reference image)"
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {uploadComplete ? 'Close' : 'Cancel'}
        </Button>
        {!uploadComplete && (
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={!selectedFile || uploading}
            startIcon={<CloudUploadIcon />}
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
